import React, { useState, useEffect, useMemo } from "react";
import { Plus, X, Truck, PackageCheck, Trash2, ChevronDown, ChevronUp, Calendar, FileText, Loader2, Check, Printer, Navigation, Paperclip, Pencil, ArrowUp, ArrowDown, ArrowRight, GripVertical } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatDateTimeIT } from "@/utils/datetime";
import JobRouteInfo from "./distinte/JobRouteInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("mv_admin_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const statusStyle = (s) => ({ aperta: "bg-blue-100 text-blue-700", completata: "bg-green-100 text-green-700" }[s] || "bg-zinc-100 text-zinc-700");
const statusLabel = (s) => ({ aperta: "Aperta", completata: "Completata", bozza: "Aperta", assegnata: "Aperta", in_corso: "Aperta" }[s] || s);
const jobStatusStyle = (s) => ({ in_attesa: "bg-zinc-100 text-zinc-700", ritirato: "bg-blue-100 text-blue-700", in_transito: "bg-orange-100 text-orange-700", in_consegna: "bg-amber-100 text-amber-700", consegnato: "bg-green-100 text-green-700" }[s] || "bg-zinc-100 text-zinc-700");
const jobStatusLabel = (s) => ({ in_attesa: "Presa in Carico", ritirato: "Ritirato", in_transito: "In Transito", in_consegna: "In Consegna", consegnato: "Consegnato" }[s] || s);

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getPrimaryStopDetails = (job) => (job._type === "shipment" ? job.dest_details : job.origin_details);
const getPrimaryCoords = (job) => (job._type === "shipment" ? job.dest_coords : job.origin_coords);

// Format any stop (origin OR dest) into a compact 2-line summary
const formatStop = (details, fallbackCity) => {
  const companyOrName = details?.company || details?.name || "";
  const street = details?.address || "";
  const locality = [details?.cap, details?.city || fallbackCity, details?.province ? `(${details.province})` : ""].filter(Boolean).join(" ");
  return {
    title: companyOrName || street || fallbackCity || "-",
    address: street || "",
    locality: locality || fallbackCity || "-",
  };
};

// Same 3-line layout as Driver App: Company -> Street -> Locality
const getPrimaryLabel = (job) => {
  const details = getPrimaryStopDetails(job);
  const fallbackCity = job._type === "shipment" ? job.destination : job.origin;
  const companyOrName = details?.company || details?.name || "";
  const street = details?.address || "";
  const locality = [details?.cap, details?.city || fallbackCity, details?.province ? `(${details.province})` : ""].filter(Boolean).join(" ");
  return {
    title: companyOrName || street || fallbackCity || "-",
    address: street || "",
    locality: locality || fallbackCity || "-",
    subtitle: details?.company && details?.name ? details.name : "",
  };
};

const totalColli = (job) => (job?.colli || []).reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) || 0;
const totalPallet = (job) => (job?.colli || []).filter((c) => (c.tipo || "").toLowerCase() === "pallet").reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) || 0;
const capacityLabel = (job) => {
  const colli = totalColli(job);
  const pallet = totalPallet(job);
  const parts = [];
  if (pallet > 0) parts.push(`${pallet} pallet`);
  if (colli > 0) parts.push(`${colli} colli`);
  return parts.join(" · ") || "0 colli";
};

const getReadyBadgeLabel = (job) => {
  if (job.goods_ready_status !== "pronta") return null;
  return job.goods_ready_time ? `Pronta alle ${job.goods_ready_time}` : "Pronta";
};

const getDdtBadgeLabel = (job) => {
  const count = job.ddt_files?.length || 0;
  if (!count) return null;
  return count > 1 ? `DDT PDF (${count})` : "DDT PDF";
};

const openPdf = (file) => {
  const binary = atob(file.data_base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const blob = new Blob([bytes], { type: file.content_type || "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

export default function AdminDistinte() {
  const [distinte, setDistinte] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [availableJobs, setAvailableJobs] = useState({ shipments: [], pickups: [] });
  const [form, setForm] = useState({ date: "", driver_id: "", note: "", shipment_ids: [], pickup_ids: [] });
  const [loading, setLoading] = useState(false);
  const [editDistinta, setEditDistinta] = useState(null);
  const [filterMonth, setFilterMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });

  const load = () => {
    const h = authHeaders();
    axios.get(`${API}/admin/distinte`, h).then((r) => setDistinte(r.data)).catch(() => {});
    axios.get(`${API}/admin/drivers`, h).then((r) => setDrivers(r.data)).catch(() => {});
    axios.get(`${API}/admin/driver-locations`, h).then((r) => setDriverLocations(r.data)).catch(() => {});
  };

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  useEffect(() => {
    if (!showCreate) return undefined;
    const refreshDriverLocations = () => axios.get(`${API}/admin/driver-locations`, authHeaders()).then((r) => setDriverLocations(r.data)).catch(() => {});
    refreshDriverLocations();
    const iv = setInterval(refreshDriverLocations, 15000);
    return () => clearInterval(iv);
  }, [showCreate]);

  const openCreate = () => {
    axios.get(`${API}/admin/distinte/available-jobs`, authHeaders()).then((r) => setAvailableJobs(r.data)).catch(() => {});
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    setForm({ date: tomorrow, driver_id: "", note: "", shipment_ids: [], pickup_ids: [] });
    setShowCreate(true);
  };

  const toggleJob = (type, id) => {
    const key = type === "shipment" ? "shipment_ids" : "pickup_ids";
    setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  };

  const createDistinta = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Seleziona una data"); return; }
    if (form.shipment_ids.length === 0 && form.pickup_ids.length === 0) { toast.error("Seleziona almeno un lavoro"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/distinte`, form, authHeaders());
      toast.success("Distinta creata!");
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  const markComplete = async (id) => {
    try {
      await axios.put(`${API}/admin/distinte/${id}`, { status: "completata" }, authHeaders());
      toast.success("Distinta completata");
      load();
    } catch { toast.error("Errore"); }
  };

  const deleteDistinta = async (id, code) => {
    if (!window.confirm(`Eliminare la distinta ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/distinte/${id}`, authHeaders());
      toast.success("Distinta eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  const assignDriver = async (id, driverId) => {
    try {
      await axios.put(`${API}/admin/distinte/${id}`, { driver_id: driverId }, authHeaders());
      toast.success("Autista assegnato!");
      load();
    } catch { toast.error("Errore"); }
  };

  const printPdf = async (id, code) => {
    try {
      const res = await axios.get(`${API}/admin/distinte/${id}/pdf`, { ...authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${code}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch { toast.error("Errore download PDF"); }
  };

  const selectedDriverLocation = useMemo(() => {
    if (!form.driver_id) return null;
    return driverLocations.find((loc) => loc.driver_id === form.driver_id) || null;
  }, [driverLocations, form.driver_id]);

  const orderedAvailableJobs = useMemo(() => {
    const jobs = [
      ...(availableJobs.shipments || []).map((job) => ({ ...job, _type: "shipment" })),
      ...(availableJobs.pickups || []).map((job) => ({ ...job, _type: "pickup" })),
    ];

    if (!selectedDriverLocation) return jobs;

    return [...jobs].sort((a, b) => {
      const aCoords = getPrimaryCoords(a);
      const bCoords = getPrimaryCoords(b);
      const aDist = aCoords ? haversineKm(selectedDriverLocation.lat, selectedDriverLocation.lng, aCoords[0], aCoords[1]) : null;
      const bDist = bCoords ? haversineKm(selectedDriverLocation.lat, selectedDriverLocation.lng, bCoords[0], bCoords[1]) : null;

      if (aDist === null && bDist === null) return 0;
      if (aDist === null) return 1;
      if (bDist === null) return -1;
      return aDist - bDist;
    });
  }, [availableJobs, selectedDriverLocation]);

  const distanceLabel = (job) => {
    if (!selectedDriverLocation) return null;
    const coords = getPrimaryCoords(job);
    if (!coords) return null;
    const km = haversineKm(selectedDriverLocation.lat, selectedDriverLocation.lng, coords[0], coords[1]);
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  };

  const filteredDistinte = useMemo(() => {
    if (!filterMonth) return distinte;
    // Match on `date` (YYYY-MM-DD) with fallback to created_at
    return distinte.filter((d) => (d.date || d.created_at || "").startsWith(filterMonth));
  }, [distinte, filterMonth]);

  return (
    <div data-testid="admin-distinte">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-heading font-bold uppercase tracking-wide text-[#09090B]">Distinte</h1>
          <p className="text-sm text-zinc-500">
            {filteredDistinte.length}{filterMonth ? ` di ${distinte.length}` : ""} distinte{filterMonth ? " filtrate" : " totali"} · Gestione giornaliere di consegna e ritiro
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="h-10 px-3 border border-zinc-200 rounded-sm text-xs font-bold text-zinc-600 bg-white"
            data-testid="distinte-month-filter"
            aria-label="Filtra distinte per mese"
          />
          {filterMonth && (
            <button
              onClick={() => setFilterMonth("")}
              className="text-xs font-bold text-red-500 hover:text-red-700 uppercase tracking-wide"
              data-testid="distinte-clear-month-filter"
            >
              Rimuovi filtro
            </button>
          )}
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-[#FF5200] text-white h-10 px-5 font-bold uppercase tracking-wide rounded-sm text-sm" data-testid="create-distinta-btn">
            <Plus size={16} /> Nuova Distinta
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Codice</th>
              <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Data</th>
              <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Autista</th>
              <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Lavori</th>
              <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Stato</th>
              <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filteredDistinte.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center">
                <FileText size={40} className="text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">{filterMonth ? `Nessuna distinta per ${filterMonth}` : "Nessuna distinta"}</p>
              </td></tr>
            ) : filteredDistinte.map((d) => (
              <React.Fragment key={d.id}>
                <tr className="hover:bg-zinc-50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === d.id ? null : d.id)} data-testid={`distinta-row-${d.code}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      {expandedRow === d.id ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                      <p className="font-bold text-sm text-[#09090B]">{d.code}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-bold flex items-center gap-1"><Calendar size={12} className="text-zinc-400" /> {d.date.split("-").reverse().join("-")}</p>
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <select value={d.driver_id || ""} onChange={(e) => { if (e.target.value) assignDriver(d.id, e.target.value); }}
                      className={`text-xs font-bold px-2 py-1.5 rounded-sm border cursor-pointer ${d.driver_id ? "border-purple-200 bg-purple-50 text-purple-700" : "border-zinc-200 text-zinc-400"}`} data-testid={`distinta-assign-${d.code}`}>
                      <option value="">{d.driver_name || "Non assegnato"}</option>
                      {drivers.map((dr) => (<option key={dr.id} value={dr.id}>{`${dr.name} ${dr.surname} (${dr.vehicle_plate})`}</option>))}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#FF5200] flex items-center gap-1"><Truck size={12} /> {d.shipment_ids?.length || 0}</span>
                      <span className="text-xs font-bold text-purple-600 flex items-center gap-1"><PackageCheck size={12} /> {d.pickup_ids?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-bold uppercase tracking-wide rounded-sm ${statusStyle(d.status)}`}>{statusLabel(d.status)}</span>
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => printPdf(d.id, d.code)} className="p-1.5 text-zinc-500 hover:text-[#FF5200] border border-zinc-200 rounded-sm" title="Stampa PDF" data-testid={`print-distinta-${d.code}`}><Printer size={14} /></button>
                      <button onClick={() => setEditDistinta(d)} className="p-1.5 text-zinc-500 hover:text-blue-600 border border-zinc-200 rounded-sm" title="Modifica distinta" data-testid={`edit-distinta-${d.code}`}><Pencil size={14} /></button>
                      {d.status !== "completata" && <button onClick={() => markComplete(d.id)} className="text-[10px] font-bold text-green-600 hover:underline uppercase px-1.5" data-testid={`complete-distinta-${d.code}`}>Completa</button>}
                      <button onClick={() => deleteDistinta(d.id, d.code)} className="p-1 text-red-400 hover:text-red-600" data-testid={`delete-distinta-${d.code}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
                {expandedRow === d.id && (
                  <tr><td colSpan={6} className="bg-zinc-50 px-6 py-4">
                    {d.note && <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-3">Note: {d.note}</p>}
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Lavori in questa distinta</p>
                    <DistintaJobsList shipmentIds={d.shipment_ids || []} pickupIds={d.pickup_ids || []} jobSequence={d.job_sequence || []} distintaId={d.id} onJobRemoved={load} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Distinta Modal */}
      {editDistinta && (
        <EditDistintaModal
          distinta={editDistinta}
          onClose={() => setEditDistinta(null)}
          onSaved={() => { setEditDistinta(null); load(); }}
        />
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10 overflow-auto" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-7xl mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="create-distinta-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
              <h2 className="font-heading font-bold text-lg uppercase">Nuova Distinta</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} className="text-zinc-400" /></button>
            </div>
            <form onSubmit={createDistinta} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="distinta-date" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1">Data *</label>
                  <input id="distinta-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm" data-testid="distinta-date-input" />
                </div>
                <div>
                  <label htmlFor="distinta-driver" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1">Autista</label>
                  <select id="distinta-driver" value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm" data-testid="distinta-driver-select">
                    <option value="">Seleziona autista</option>
                    {drivers.map((d) => (<option key={d.id} value={d.id}>{`${d.name} ${d.surname} (${d.vehicle_plate})`}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="distinta-note" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1">Note</label>
                <input id="distinta-note" type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm" placeholder="Note per l'autista" />
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 flex items-center gap-1">
                    <Navigation size={12} className="text-[#FF5200]" /> Lavori ordinati per vicinanza
                  </p>
                  <p className="text-[11px] text-zinc-500" data-testid="admin-route-sorting-status">
                    {selectedDriverLocation
                      ? `Posizione ${selectedDriverLocation.driver_name}: ${selectedDriverLocation.lat.toFixed(4)}, ${selectedDriverLocation.lng.toFixed(4)}`
                      : "Seleziona un autista con posizione attiva per l’ordine automatico; altrimenti resta l’ordine attuale."}
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-auto border border-zinc-200 rounded">
                  {orderedAvailableJobs.length === 0 ? (
                    <p className="p-3 text-xs text-zinc-400 text-center">Nessun lavoro disponibile</p>
                  ) : orderedAvailableJobs.map((job, index) => {
                    const selected = job._type === "shipment" ? form.shipment_ids.includes(job.id) : form.pickup_ids.includes(job.id);
                    return (
                      <label key={job.id} className={`flex items-start gap-3 px-3 py-2.5 border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50 ${selected ? (job._type === "shipment" ? "bg-orange-50" : "bg-purple-50") : ""}`}>
                        <input type="checkbox" checked={selected} onChange={() => toggleJob(job._type, job.id)} className={job._type === "shipment" ? "accent-[#FF5200]" : "accent-purple-600"} />
                        <div className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${job._type === "shipment" ? "bg-orange-100 text-[#FF5200]" : "bg-purple-100 text-purple-700"}`}>{index + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            {job._type === "shipment" ? <Truck size={12} className="text-[#FF5200]" /> : <PackageCheck size={12} className="text-purple-600" />}
                            <span className="text-[10px] uppercase tracking-wide text-zinc-400">{job._type === "shipment" ? "Spedizione" : "Ritiro"}</span>
                            <span className="text-[10px] text-zinc-400">· {job.tracking_number}</span>
                            {distanceLabel(job) && <span className="text-[10px] font-bold text-[#FF5200]">· {distanceLabel(job)}</span>}
                          </div>
                          <JobRouteInfo job={job} idx={index} testPrefix="create-job" />
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="bg-zinc-50 p-3 rounded flex items-center justify-between">
                <span className="text-xs text-zinc-600">Selezionati: <b className="text-[#FF5200]">{form.shipment_ids.length} spedizioni</b> + <b className="text-purple-600">{form.pickup_ids.length} ritiri</b></span>
              </div>

              <button type="submit" disabled={loading}
                className="w-full h-11 bg-[#FF5200] text-white font-bold uppercase tracking-wide rounded-sm text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-distinta-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} /> Crea Distinta</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function EditDistintaModal({ distinta, onClose, onSaved }) {
  const [available, setAvailable] = useState({ shipments: [], pickups: [] });
  const [shipmentIds, setShipmentIds] = useState([]);
  const [pickupIds, setPickupIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [orderedJobs, setOrderedJobs] = useState([]); // [{type, id, data}] in display order
  const [reordering, setReordering] = useState(false);
  const [editDate, setEditDate] = useState(distinta.date || "");
  const [savingDate, setSavingDate] = useState(false);

  const persistDate = async (newDate) => {
    if (!newDate || newDate === distinta.date) return;
    setSavingDate(true);
    try {
      await axios.put(`${API}/admin/distinte/${distinta.id}`, { date: newDate }, authHeaders());
      toast.success("Data distinta aggiornata");
      distinta.date = newDate; // local mutation so UI reflects change before parent reload
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento data");
      setEditDate(distinta.date || "");
    } finally {
      setSavingDate(false);
    }
  };

  const reload = () => {
    const h = authHeaders();
    axios.get(`${API}/admin/distinte/${distinta.id}/editable-jobs`, h).then((r) => setAvailable(r.data)).catch(() => {});
    axios.get(`${API}/admin/distinte/${distinta.id}/jobs`, h)
      .then((r) => setOrderedJobs(r.data || []))
      .catch(() => setOrderedJobs([]));
  };

  useEffect(() => { reload(); }, [distinta.id]);

  const toggleAdd = (type, id) => {
    if (type === "shipment") setShipmentIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
    else setPickupIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const persistOrder = async (newList) => {
    setReordering(true);
    try {
      await axios.put(`${API}/admin/distinte/${distinta.id}/reorder`, { sequence: newList.map((x) => ({ type: x.type, id: x.id })) }, authHeaders());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore riordino");
      reload();
    } finally {
      setReordering(false);
    }
  };

  const moveJob = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= orderedJobs.length) return;
    const next = orderedJobs.slice();
    [next[index], next[target]] = [next[target], next[index]];
    setOrderedJobs(next);
    persistOrder(next);
  };

  // HTML5 drag-and-drop for desktop
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch (_) { /* noop */ }
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from === null || from === targetIdx) return;
    const next = orderedJobs.slice();
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setOrderedJobs(next);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  // Editable note
  const [noteValue, setNoteValue] = useState(distinta.note || "");
  const [savingNote, setSavingNote] = useState(false);
  const persistNote = async () => {
    if (noteValue === (distinta.note || "")) return;
    setSavingNote(true);
    try {
      await axios.put(`${API}/admin/distinte/${distinta.id}`, { note: noteValue }, authHeaders());
      distinta.note = noteValue;
      toast.success("Note aggiornate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio note");
      setNoteValue(distinta.note || "");
    } finally {
      setSavingNote(false);
    }
  };

  const removeJob = async (jobId, jobType) => {
    if (!window.confirm("Rimuovere questo lavoro dalla distinta?")) return;
    setRemoving(jobId);
    try {
      await axios.post(`${API}/admin/distinte/${distinta.id}/remove-job`, { job_id: jobId, job_type: jobType }, authHeaders());
      toast.success("Lavoro rimosso");
      setOrderedJobs((arr) => arr.filter((x) => !(x.type === jobType && x.id === jobId)));
      reload();
    } catch { toast.error("Errore rimozione"); }
    finally { setRemoving(null); }
  };

  const saveAdditions = async () => {
    if (shipmentIds.length === 0 && pickupIds.length === 0) {
      onSaved();
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/distinte/${distinta.id}/add-jobs`, { shipment_ids: shipmentIds, pickup_ids: pickupIds }, authHeaders());
      toast.success("Distinta aggiornata. Il PDF verrà rigenerato alla stampa.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const availableJobs = [
    ...(available.shipments || []).map((j) => ({ ...j, _type: "shipment" })),
    ...(available.pickups || []).map((j) => ({ ...j, _type: "pickup" })),
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10 overflow-auto" onClick={onClose}>
      <div className="bg-white border border-zinc-200 w-full max-w-7xl mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-distinta-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div className="flex-1">
            <h2 className="font-heading font-bold text-lg uppercase">Modifica Distinta {distinta.code}</h2>
            <div className="flex items-center gap-4 mt-1 flex-wrap">
              <label className="flex items-center gap-2 text-xs">
                <span className="font-bold uppercase tracking-widest text-zinc-500">Data:</span>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  onBlur={(e) => persistDate(e.target.value)}
                  disabled={savingDate}
                  className="h-8 px-2 border border-zinc-300 rounded-sm text-xs disabled:opacity-50"
                  data-testid="edit-distinta-date"
                />
                {savingDate && <span className="text-[10px] text-zinc-400">Salvataggio...</span>}
              </label>
              <p className="text-xs text-zinc-500">Autista: {distinta.driver_name || "Non assegnato"}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-edit-distinta"><X size={20} className="text-zinc-400" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current jobs (with reorder + remove) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold tracking-widest uppercase text-zinc-500">Ordine lavori in distinta ({orderedJobs.length})</p>
              <p className="text-[10px] text-zinc-400">Trascina ⋮⋮ o usa ▲▼</p>
            </div>
            <div className="border border-zinc-200 rounded divide-y divide-zinc-100 max-h-[55vh] overflow-auto">
              {orderedJobs.length === 0 && (
                <p className="p-3 text-xs text-zinc-400 text-center">Nessun lavoro nella distinta</p>
              )}
              {orderedJobs.map((entry, index) => {
                const job = entry.data;
                const isShip = entry.type === "shipment";
                const isDragging = dragIdx === index;
                const isDragOver = dragOverIdx === index && dragIdx !== index;
                return (
                  <div
                    key={`${entry.type}-${entry.id}`}
                    draggable={!reordering}
                    onDragStart={onDragStart(index)}
                    onDragOver={onDragOver(index)}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop(index)}
                    onDragEnd={onDragEnd}
                    className={`px-3 py-2.5 flex items-start gap-3 transition-all ${isDragging ? "opacity-40" : ""} ${isDragOver ? "bg-orange-50 border-t-2 border-[#FF5200]" : ""}`}
                    data-testid={`edit-current-${entry.type}-${job.tracking_number}`}
                  >
                    <div className="flex flex-col items-center gap-0.5 mt-0.5">
                      <span className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500" title="Trascina per riordinare" data-testid={`drag-handle-${job.tracking_number}`}>
                        <GripVertical size={14} />
                      </span>
                      <button type="button" onClick={() => moveJob(index, -1)} disabled={index === 0 || reordering}
                        className="p-0.5 border border-zinc-200 rounded-sm text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
                        title="Sposta su" data-testid={`move-up-${job.tracking_number}`}>
                        <ArrowUp size={12} />
                      </button>
                      <button type="button" onClick={() => moveJob(index, 1)} disabled={index === orderedJobs.length - 1 || reordering}
                        className="p-0.5 border border-zinc-200 rounded-sm text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
                        title="Sposta giù" data-testid={`move-down-${job.tracking_number}`}>
                        <ArrowDown size={12} />
                      </button>
                    </div>
                    <div className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center mt-0.5 ${isShip ? "bg-orange-100 text-[#FF5200]" : "bg-purple-100 text-purple-700"}`}>{index + 1}</div>
                    {isShip ? <Truck size={14} className="text-[#FF5200] mt-1" /> : <PackageCheck size={14} className="text-purple-500 mt-1" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-[10px] uppercase tracking-wide text-zinc-400">{isShip ? "Spedizione" : "Ritiro"} · {job.tracking_number}</p>
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-sm ${jobStatusStyle(job.status)}`}
                          data-testid={`edit-job-status-${job.tracking_number}`}
                        >
                          {jobStatusLabel(job.status)}
                        </span>
                      </div>
                      <JobRouteInfo job={job} idx={index} testPrefix="edit-current" />
                    </div>
                    <button onClick={() => removeJob(entry.id, entry.type)} disabled={removing === entry.id}
                      className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase px-2 py-1 border border-red-200 rounded-sm hover:bg-red-50 disabled:opacity-50"
                      data-testid={`edit-remove-job-${entry.id}`}>{removing === entry.id ? "..." : "Rimuovi"}</button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Available jobs to add */}
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2">Aggiungi lavori disponibili ({availableJobs.length})</p>
            <div className="border border-zinc-200 rounded max-h-[55vh] overflow-auto">
              {availableJobs.length === 0 ? (
                <p className="p-3 text-xs text-zinc-400 text-center">Nessun lavoro disponibile da aggiungere</p>
              ) : availableJobs.map((job, idx) => {
                const selected = job._type === "shipment" ? shipmentIds.includes(job.id) : pickupIds.includes(job.id);
                return (
                  <label key={job.id} className={`flex items-start gap-3 px-3 py-2.5 border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50 ${selected ? (job._type === "shipment" ? "bg-orange-50" : "bg-purple-50") : ""}`} data-testid={`edit-add-job-${job.tracking_number}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleAdd(job._type, job.id)} className={job._type === "shipment" ? "accent-[#FF5200]" : "accent-purple-600"} />
                    <div className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${job._type === "shipment" ? "bg-orange-100 text-[#FF5200]" : "bg-purple-100 text-purple-700"}`}>{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        {job._type === "shipment" ? <Truck size={12} className="text-[#FF5200]" /> : <PackageCheck size={12} className="text-purple-600" />}
                        <span className="text-[10px] uppercase tracking-wide text-zinc-400">{job._type === "shipment" ? "Spedizione" : "Ritiro"}</span>
                        <span className="text-[10px] text-zinc-400">· {job.tracking_number}</span>
                      </div>
                      <JobRouteInfo job={job} idx={idx} testPrefix="edit-add" />
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          </div>

          <div className="border border-zinc-200 rounded p-3">
            <label className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 block">Note distinta</label>
            <textarea
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              onBlur={persistNote}
              placeholder="Aggiungi note alla distinta (es. consegne preferenziali, contatti, indicazioni speciali)..."
              className="w-full min-h-[70px] px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none resize-y"
              data-testid="edit-distinta-note"
              disabled={savingNote}
            />
            {savingNote && <p className="text-[10px] text-zinc-400 mt-1">Salvataggio note...</p>}
          </div>

          <div className="bg-zinc-50 p-3 rounded flex items-center justify-between">
            <span className="text-xs text-zinc-600">Da aggiungere: <b className="text-[#FF5200]">{shipmentIds.length} spedizioni</b> + <b className="text-purple-600">{pickupIds.length} ritiri</b></span>
            <button onClick={saveAdditions} disabled={saving} className="h-10 px-5 bg-[#FF5200] text-white font-bold uppercase tracking-wide rounded-sm text-xs disabled:opacity-50 flex items-center gap-2" data-testid="save-edit-distinta-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Salva e chiudi</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DistintaJobsList({ shipmentIds, pickupIds, jobSequence, distintaId, onJobRemoved }) {
  const [orderedJobs, setOrderedJobs] = useState([]); // [{type, id, data}]
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/distinte/${distintaId}/jobs`, authHeaders())
      .then((r) => {
        if (cancelled) return;
        setOrderedJobs(r.data || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOrderedJobs([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [distintaId, shipmentIds, pickupIds, jobSequence]);

  const removeJob = async (jobId, jobType) => {
    if (!window.confirm("Disimpegnare questo lavoro dalla distinta? Sara disponibile per altre distinte.")) return;
    setRemoving(jobId);
    try {
      await axios.post(`${API}/admin/distinte/${distintaId}/remove-job`, { job_id: jobId, job_type: jobType }, authHeaders());
      toast.success("Lavoro disimpegnato dalla distinta");
      if (onJobRemoved) onJobRemoved();
    } catch { toast.error("Errore nel disimpegno"); }
    finally { setRemoving(null); }
  };

  if (loading && orderedJobs.length === 0) {
    return <p className="text-xs text-zinc-400 py-2">Caricamento...</p>;
  }
  if (orderedJobs.length === 0) {
    return <p className="text-xs text-zinc-400 py-2">Nessun lavoro assegnato</p>;
  }

  return (
    <div className="space-y-1.5">
      {orderedJobs.map((entry, idx) => {
        const job = entry.data;
        const isShip = entry.type === "shipment";
        const from = formatStop(job.origin_details, job.origin);
        const to = formatStop(job.dest_details, job.destination);
        return (
          <div key={`${entry.type}-${entry.id}`} className="bg-white border border-zinc-200 rounded p-3 flex items-start justify-between gap-3" data-testid={`distinta-job-row-${idx}`}>
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <div className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5 ${isShip ? "bg-orange-100 text-[#FF5200]" : "bg-purple-100 text-purple-700"}`}>{idx + 1}</div>
              {isShip ? <Truck size={14} className="text-[#FF5200] mt-1 flex-shrink-0" /> : <PackageCheck size={14} className="text-purple-500 mt-1 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-zinc-400">{isShip ? "Spedizione" : "Ritiro"} · {job.tracking_number} {job.ddt_reference && <span className="text-zinc-400">· DDT: {job.ddt_reference}</span>}</p>

                {/* Mittente → Destinatario sulla stessa riga */}
                <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 mt-1" data-testid={`distinta-job-route-${idx}`}>
                  <div className="min-w-0" data-testid={`distinta-job-from-${idx}`}>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Mittente</p>
                    <p className="text-xs font-bold text-[#09090B] uppercase truncate">{from.title}</p>
                    {from.address && <p className="text-[11px] text-zinc-700 truncate">{from.address}</p>}
                    <p className="text-[11px] text-zinc-500 truncate">{from.locality}</p>
                  </div>
                  <ArrowRight size={14} className="text-zinc-400 mt-4 flex-shrink-0" />
                  <div className="min-w-0" data-testid={`distinta-job-to-${idx}`}>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-[#FF5200]">Destinatario</p>
                    <p className="text-xs font-bold text-[#09090B] uppercase truncate">{to.title}</p>
                    {to.address && <p className="text-[11px] text-zinc-700 truncate">{to.address}</p>}
                    <p className="text-[11px] text-zinc-500 truncate">{to.locality}</p>
                  </div>
                </div>

                <p className="text-[10px] font-bold text-zinc-600 mt-1.5">{capacityLabel(job)}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {(() => {
                    const u = job.urgency === "urgente" ? { label: "URGENTE", cls: "bg-red-100 text-red-700 border border-red-300" }
                      : job.urgency === "express" ? { label: "EXPRESS", cls: "bg-orange-100 text-orange-700 border border-orange-300" }
                      : { label: "NORMALE", cls: "bg-zinc-100 text-zinc-600 border border-zinc-200" };
                    return <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${u.cls}`} data-testid={`distinta-urgency-${idx}`}>{u.label}</span>;
                  })()}
                  {job.service_type && <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-500 bg-zinc-50 rounded-sm border border-zinc-200">{job.service_type}</span>}
                  {getReadyBadgeLabel(job) && <span className="px-2 py-1 bg-green-50 text-green-700 text-[11px] font-bold rounded-sm">{getReadyBadgeLabel(job)}</span>}
                  {getDdtBadgeLabel(job) && <button type="button" onClick={() => openPdf(job.ddt_files[0])} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 text-[11px] font-bold rounded-sm hover:bg-zinc-200"><Paperclip size={12} /> {getDdtBadgeLabel(job)}</button>}
                </div>
                {job.descrizione_merce && <p className="text-[11px] text-zinc-700 mt-1.5"><span className="font-bold uppercase text-zinc-500 text-[9px] tracking-widest">Merce:</span> {job.descrizione_merce}</p>}
                {job.note && <p className="text-[11px] text-amber-700 mt-1"><span className="font-bold uppercase text-amber-600 text-[9px] tracking-widest">Note:</span> {job.note}</p>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span
                className={`px-2.5 py-1 text-[11px] font-bold uppercase rounded-sm ${jobStatusStyle(job.status)}`}
                data-testid={`distinta-job-status-${job.tracking_number}`}
              >
                {jobStatusLabel(job.status)}
              </span>
              {(job.status === "consegnato" || job.status === "ritirato") && (() => {
                const hist = Array.isArray(job.status_history) ? job.status_history : [];
                const evt = [...hist].reverse().find((h) => h.status === job.status);
                return evt?.timestamp ? (
                  <span className="text-[10px] text-zinc-500">{formatDateTimeIT(evt.timestamp)}</span>
                ) : null;
              })()}
              {job.pod && job.tracking_number && (
                <a
                  href={`${API}/tracking/${job.tracking_number}/pod`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] font-bold text-green-700 hover:text-green-900 uppercase px-1.5 border border-green-300 rounded-sm hover:bg-green-50"
                  title="Scarica POD PDF"
                  data-testid={`distinta-download-pod-${job.tracking_number}`}
                >
                  POD
                </a>
              )}
              <button onClick={() => removeJob(entry.id, entry.type)} disabled={removing === entry.id}
                className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase px-1.5 border border-red-200 rounded-sm hover:bg-red-50 disabled:opacity-50"
                data-testid={`remove-job-${entry.id}`} title="Disimpegna dalla distinta">
                {removing === entry.id ? "..." : "Disimpegna"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
